import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CreditCard, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export function PaymentLinkButton({ deal, onCreated, compact }) {
  const [busy, setBusy] = useState(false);
  if (deal.paid_at) return <span className="inline-flex items-center gap-1 text-[10px] text-emerald-500" data-testid={`deal-paid-${deal.id}`}><CheckCircle2 className="w-3 h-3" />Paid</span>;
  const copy = async (url) => { try { await navigator.clipboard.writeText(url); toast.success("Payment link copied"); } catch { toast.info(url); } };
  const create = async (e) => {
    e.stopPropagation();
    if (deal.payment_link) { copy(deal.payment_link); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/deals/${deal.id}/payment-link`, { origin_url: window.location.origin });
      toast.success(`Payment link ready for $${data.amount.toLocaleString()}`);
      copy(data.checkout_url);
      onCreated?.(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const I = deal.payment_link ? Copy : CreditCard;
  return (
    <Button type="button" variant="ghost" size={compact ? "icon" : "sm"} className={compact ? "h-6 w-6" : "h-7 text-xs"} onClick={create} disabled={busy} title={deal.payment_link ? "Copy payment link" : "Create Stripe payment link"} data-testid={`deal-pay-${deal.id}`}>
      <I className="w-3.5 h-3.5" />{!compact && <span className="ml-1.5">{deal.payment_link ? "Copy pay link" : "Payment link"}</span>}
    </Button>
  );
}

export function PaymentResult({ success }) {
  const [state, setState] = useState("checking");
  const [info, setInfo] = useState(null);
  React.useEffect(() => {
    if (!success) { setState("cancel"); return; }
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) { setState("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (err) { if (err.response?.status === 404) { setState("error"); return; } }
      if (++tries < 8) setTimeout(poll, 2000); else setState("pending");
    };
    poll();
  }, [success]);
  const msg = { checking: ["Confirming your payment…", "This usually takes a few seconds."], paid: [info?.kind === "showing" ? "Showing request confirmed — thank you!" : "Payment received — thank you!", info ? (info.kind === "showing" ? `$${info.amount.toLocaleString()} showing fee received. We're scheduling your tour with a licensed showing agent through Showami and will text/email you the confirmed time.` : `$${info.amount.toLocaleString()} ${info.currency.toUpperCase()} · the deal has been marked as won.`) : ""], pending: ["Payment is processing", "We'll update the deal as soon as Stripe confirms."], cancel: ["Payment cancelled", "No charge was made. You can reopen the link any time."], error: ["We couldn't confirm this payment", "Check the deal in Auto-Close-Ai or try the link again."] }[state];
  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <div className="max-w-md w-full border border-border rounded-md bg-card p-8 space-y-3" data-testid={`payment-${state}`}>
        <div className={`text-2xl font-semibold tracking-tight ${state === "paid" ? "text-emerald-500" : ""}`}>{msg[0]}</div>
        <div className="text-sm text-muted-foreground">{msg[1]}</div>
        {info?.kind !== "showing" && <a href="/deals" className="inline-block text-sm text-primary mt-2" data-testid="payment-back">Back to deals →</a>}
      </div>
    </div>
  );
}
